import { IPlan } from '../../components/Plans/Plans';
import { api } from './api';

const LOCATION_LIST_KEY: Record<string, string> = {
  airport: 'airports',
  city: 'cities',
  country: 'countries',
};

function locationItemDisplayName(item: unknown): string {
  if (item == null) return '';
  if (typeof item === 'string') return item.trim();
  if (typeof item !== 'object') return String(item);
  const o = item as Record<string, unknown>;
  for (const key of ['name', 'title', 'label', 'full_name'] as const) {
    const v = o[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  const municipality =
    typeof o.municipality === 'string'
      ? o.municipality.trim()
      : typeof o.city === 'string'
        ? o.city.trim()
        : '';
  const code =
    typeof o.iata_code === 'string'
      ? o.iata_code
      : typeof o.iata === 'string'
        ? o.iata
        : typeof o.code === 'string'
          ? o.code
          : '';
  if (municipality && code) return `${municipality} (${code})`;
  if (municipality) return municipality;
  if (code) return code;
  return '';
}

function normalizeLocationsResponse(response: unknown, type: string): { name: string }[] {
  let raw: unknown[] = [];

  if (Array.isArray(response)) {
    raw = response;
  } else if (response && typeof response === 'object') {
    const o = response as Record<string, unknown>;
    const listKey = LOCATION_LIST_KEY[type];
    if (listKey && Array.isArray(o[listKey])) {
      raw = o[listKey] as unknown[];
    } else {
      const firstArray = Object.values(o).find((v): v is unknown[] => Array.isArray(v));
      if (firstArray) raw = firstArray;
    }
  }

  const seen = new Set<string>();
  const out: { name: string }[] = [];
  for (const item of raw) {
    const name = locationItemDisplayName(item);
    if (!name || seen.has(name)) continue;
    seen.add(name);
    out.push({ name });
  }
  return out;
}

export const auth = api.injectEndpoints({
  endpoints: builder => ({
    getLocations: builder.query<{ name: string }[], { search: string; type: string }>({
      query: ({ search, type }) => ({
        url: `${process.env.REACT_APP_API_URL}/users/locations/?search=${search}&type=${type}`,
      }),
      transformResponse: (response: unknown, _meta, arg) => normalizeLocationsResponse(response, arg.type),
      keepUnusedDataFor: 5,
    }),
    getPlans: builder.query<IPlan[], void>({
      query: () => ({ url: `${process.env.REACT_APP_API_URL}/users/plans/` }),
      keepUnusedDataFor: 5,
    }),
    loginUser: builder.mutation({
      query: ({ email, password }) => ({
        url: `${process.env.REACT_APP_API_URL}/users/sign-in/`,
        method: 'POST',
        body: { email, password },
      }),
    }),
    loginUserConfirm: builder.mutation({
      query: ({ email, confirmation_code }) => ({
        url: `${process.env.REACT_APP_API_URL}/users/sign-in/confirm/`,
        method: 'POST',
        body: { email, confirmation_code },
      }),
    }),
    signUpInitial: builder.mutation<
      unknown,
      {
        email: string;
        password: string;
        first_name: string;
        last_name: string;
        referral_token?: string;
      }
    >({
      query: body => ({
        url: `${process.env.REACT_APP_API_URL}/users/sign-up/initial/`,
        method: 'POST',
        body,
      }),
    }),
    signUpComplete: builder.mutation<
      unknown,
      { username: string; gender: string; age_group: string; country: string }
    >({
      query: body => ({
        url: `${process.env.REACT_APP_API_URL}/users/sign-up/complete/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    signUpResendCode: builder.mutation<unknown, { email: string }>({
      query: body => ({
        url: `${process.env.REACT_APP_API_URL}/users/sign-up/resend-code/`,
        method: 'POST',
        body,
      }),
    }),
    changePassword: builder.mutation({
      query: ({ current_password, new_password, confirm_password }) => ({
        url: `${process.env.REACT_APP_API_URL}/users/me/change-password/`,
        method: 'POST',
        body: {
          current_password,
          new_password,
          confirm_password
        },
      }),
    }),
    resetPasswordInitiate: builder.mutation({
      query: ({ email }) => ({
        url: `${process.env.REACT_APP_API_URL}/users/reset-password/initiate/`,
        method: 'POST',
        body: {
          email,
        },
      }),
    }),
    validateCode: builder.mutation({
      query: ({ token }) => ({
        url: `${process.env.REACT_APP_API_URL}/users/reset-password/validate/`,
        method: 'POST',
        body: {
          token,
        },
      }),
    }),
    resetPassword: builder.mutation({
      query: ({ token, password }) => ({
        url: `${process.env.REACT_APP_API_URL}/users/reset-password/`,
        method: 'POST',
        body: {
          token,
          password,
        },
      }),
    }),
    deleteAccount: builder.mutation<void, void>({
      query: () => ({
        url: `${process.env.REACT_APP_API_URL}/users/me/`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useGetLocationsQuery,
  useGetPlansQuery,
  useLoginUserMutation,
  useSignUpInitialMutation,
  useSignUpCompleteMutation,
  useSignUpResendCodeMutation,
  useChangePasswordMutation,
  useResetPasswordInitiateMutation,
  useValidateCodeMutation,
  useResetPasswordMutation,
  useLoginUserConfirmMutation,
  useDeleteAccountMutation,
} = auth;
